import { PdfTextLine } from './pdfTextLines';
import { isTenderPositionNumber, parseTenderQuantity } from '../tenderLvImport';

/**
 * Spalten eines LV-Formulars aus der Textebene erkennen und mit einer
 * Bedeutung belegen.
 *
 * Der Sinn: sind die Spalten einmal bestimmt, ist das Auslesen einer Zeile
 * reine Geometrie — kein Modell muss mehr raten, welcher Textschnipsel die
 * Menge ist. Genau daran ist der KI-Import gescheitert.
 *
 * Die Rollen werden vorgeschlagen, nicht festgelegt: der Nutzer korrigiert sie
 * im Markiermodus mit vier Klicks, und ab da gilt die Zuordnung fuer ALLE
 * Seiten — LV-Formulare wechseln ihr Raster innerhalb eines Dokuments nicht.
 *
 * Rein und ohne pdfjs/DOM.
 */

export type ColumnRole = 'ignore' | 'pos' | 'bezeichnung' | 'menge' | 'einheit';

export const COLUMN_ROLE_LABELS: Record<ColumnRole, string> = {
    ignore: 'nicht übernehmen',
    pos: 'LV-Nummer',
    bezeichnung: 'Bezeichnung',
    menge: 'Menge',
    einheit: 'Einheit',
};

export interface ColumnBand {
    /** Linke und rechte Kante im PDF-User-Space. */
    x0: number;
    x1: number;
    role: ColumnRole;
}

/**
 * Zwei Textbloecke gehoeren zur selben Spalte, wenn ihre Startpunkte naeher
 * beieinander liegen als das. In pt; LV-Formulare setzen ihre Spalten
 * typischerweise mit deutlich mehr Abstand.
 */
const CLUSTER_GAP = 14;
/** Unter dieser Trefferzahl ist ein Cluster Zufall (Kopfzeile, Fussnote). */
const MIN_HITS = 3;

/** Gaengige Mengeneinheiten im Bauwesen — Grundlage der Rollen-Vermutung. */
const UNIT_TOKENS = new Set([
    'stk', 'st', 'stck', 'stück', 'stueck', 'psch', 'pausch', 'pauschal', 'pa',
    'm', 'm2', 'm²', 'm3', 'm³', 'lfm', 'lfdm', 'mm', 'cm', 'km',
    'kg', 't', 'to', 'l', 'h', 'std', 'std.', 'stunde', 'stunden',
    'tag', 'tage', 'wo', 'woche', 'wochen', 'mon', 'monat', 'monate', 'ea', 'psc',
]);

export const isUnitToken = (s: string): boolean =>
    UNIT_TOKENS.has(s.trim().toLowerCase().replace(/\.$/, ''));

/**
 * Spaltenbaender aus den Startpunkten aller Textitems clustern.
 *
 * Bewusst ueber die Item-Startpunkte und nicht ueber die Zeilen: eine Spalte
 * verraet sich dadurch, dass ueber viele Zeilen hinweg immer wieder an
 * derselben x-Position etwas anfaengt.
 */
export const detectColumnBands = (lines: PdfTextLine[]): ColumnBand[] => {
    const points: { x: number; end: number }[] = [];
    for (const line of lines) {
        for (const it of line.items) {
            if (!it.str.trim()) continue;
            points.push({ x: it.x, end: it.x + it.width });
        }
    }
    if (!points.length) return [];

    points.sort((a, b) => a.x - b.x);
    const clusters: { x0: number; x1: number; hits: number }[] = [];
    for (const p of points) {
        const last = clusters[clusters.length - 1];
        if (last && p.x - last.x0 <= CLUSTER_GAP) {
            last.x1 = Math.max(last.x1, p.end);
            last.hits++;
        } else {
            clusters.push({ x0: p.x, x1: p.end, hits: 1 });
        }
    }

    const kept = clusters.filter((c) => c.hits >= MIN_HITS);
    if (!kept.length) return [];

    // Trennkanten zwischen den Spalten. Die rechte Kante eines Clusters wird
    // dabei am Beginn der naechsten Spalte GEKAPPT: eine einzelne breite Zeile
    // — die Titelzeile laeuft in LV-Formularen ueber die Nummernspalte hinaus —
    // dehnte das Cluster sonst ueber die Nachbarspalte, und die Trennkante
    // rutschte hinter deren Inhalt. Die ganze Bezeichnungsspalte fiel dadurch
    // in die Nummernspalte.
    const edges: number[] = [];
    for (let i = 0; i < kept.length - 1; i++) {
        const nextStart = kept[i + 1].x0;
        const cappedEnd = Math.min(kept[i].x1, nextStart);
        edges.push((cappedEnd + nextStart) / 2);
    }

    return kept.map((_, i) => ({
        x0: i === 0 ? -Infinity : edges[i - 1],
        x1: i === kept.length - 1 ? Infinity : edges[i],
        role: 'ignore' as ColumnRole,
    }));
};

/** Index des Bandes, in das eine x-Position faellt. -1, wenn keines passt. */
export const bandIndexAt = (bands: ColumnBand[], x: number): number =>
    bands.findIndex((b) => x >= b.x0 && x < b.x1);

/** Der Text einer Zeile, der in ein bestimmtes Band faellt. */
export const bandText = (line: PdfTextLine, band: ColumnBand): string => {
    const parts = line.items.filter((it) => it.x >= band.x0 && it.x < band.x1).map((it) => it.str);
    return parts.join('').replace(/\s+/g, ' ').trim();
};

/**
 * Rollen vorschlagen: welches Band traegt Nummern, welches Mengen, welches
 * Einheiten? Alles daneben ist Bezeichnung.
 *
 * Bewusst rein statistisch. Spaltenkoepfe ("Pos.", "Menge", "ME") stehen nicht
 * in jedem LV, die Spalteninhalte schon.
 */
export const guessColumnRoles = (bands: ColumnBand[], lines: PdfTextLine[]): ColumnBand[] => {
    if (!bands.length) return bands;

    const stats = bands.map(() => ({ pos: 0, num: 0, unit: 0, chars: 0, filled: 0 }));
    for (const line of lines) {
        for (let b = 0; b < bands.length; b++) {
            const t = bandText(line, bands[b]);
            if (!t) continue;
            stats[b].filled++;
            stats[b].chars += t.length;
            if (isTenderPositionNumber(t)) stats[b].pos++;
            if (isUnitToken(t)) stats[b].unit++;
            // Eine Menge ist eine reine Zahl — "12,50" ja, "12,5 m²" nein
            // (das waere eine zusammengefallene Menge-/Einheit-Spalte, die
            // spaeter beim Auslesen ohnehin aufgetrennt wird).
            if (/^-?[\d.,]+$/.test(t) && parseTenderQuantity(t) > 0) stats[b].num++;
        }
    }

    /**
     * @param leftmost Bei `true` gewinnt das am weitesten links liegende
     *   qualifizierte Band statt des trefferstaerksten. Fuer die Nummernspalte
     *   noetig: `isTenderPositionNumber('24')` ist wahr, eine Mengenspalte mit
     *   lauter Ganzzahlen haette sonst mehr "Treffer" als die Nummernspalte
     *   und die Mengen waeren als LV-Nummern ins Angebot gewandert.
     */
    const pick = (key: 'pos' | 'num' | 'unit', taken: Set<number>, leftmost = false): number => {
        let best = -1;
        let bestVal = 0;
        for (let i = 0; i < bands.length; i++) {
            if (taken.has(i)) continue;
            const v = stats[i][key];
            // Mindestens ein Drittel der befuellten Zellen muss passen, sonst
            // ist es keine Spalte dieser Art.
            const qualifies = stats[i].filled > 0 && v >= Math.max(2, stats[i].filled / 3);
            if (!qualifies) continue;
            if (leftmost) return i;
            if (v > bestVal) { best = i; bestVal = v; }
        }
        return best;
    };

    const taken = new Set<number>();
    const out = bands.map((b) => ({ ...b }));

    const posIdx = pick('pos', taken, true);
    if (posIdx >= 0) { out[posIdx].role = 'pos'; taken.add(posIdx); }
    const unitIdx = pick('unit', taken);
    if (unitIdx >= 0) { out[unitIdx].role = 'einheit'; taken.add(unitIdx); }
    const numIdx = pick('num', taken);
    if (numIdx >= 0) { out[numIdx].role = 'menge'; taken.add(numIdx); }

    // Bezeichnung = das textreichste verbleibende Band.
    let textIdx = -1;
    let textChars = 0;
    for (let i = 0; i < bands.length; i++) {
        if (taken.has(i)) continue;
        if (stats[i].chars > textChars) { textChars = stats[i].chars; textIdx = i; }
    }
    if (textIdx >= 0) out[textIdx].role = 'bezeichnung';

    return out;
};

export interface LvCells {
    position: string;
    bezeichnung: string;
    einheit: string;
    menge: number;
    /** Rohtext der Mengenzelle — fuer die Anzeige im Review. */
    mengeText: string;
}

/**
 * Eine Zeile anhand der Spaltenrollen auslesen.
 *
 * Ist keine Bezeichnungsspalte gesetzt, gilt der ganze Zeilentext abzueglich
 * der belegten Spalten — so liefert der Modus auch dann etwas Brauchbares,
 * wenn die Rollen noch nicht gesetzt sind.
 */
export const readLineCells = (line: PdfTextLine, bands: ColumnBand[]): LvCells => {
    const byRole = new Map<ColumnRole, ColumnBand[]>();
    bands.forEach((b) => {
        const list = byRole.get(b.role) ?? [];
        list.push(b);
        byRole.set(b.role, list);
    });

    const textOf = (role: ColumnRole): string =>
        (byRole.get(role) ?? []).map((b) => bandText(line, b)).filter(Boolean).join(' ').trim();

    const position = textOf('pos');
    let einheit = textOf('einheit');
    const mengeText = textOf('menge');
    let menge = parseTenderQuantity(mengeText);

    // Menge und Einheit in EINER Zelle ("12,5 m²") — der haeufigste
    // Sonderfall in schmal gesetzten Formularen.
    if (mengeText && !einheit) {
        const m = /^([\d.,\s]+)\s*([a-zA-Zä-üÄ-Ü²³.]+)$/.exec(mengeText);
        if (m && isUnitToken(m[2])) {
            menge = parseTenderQuantity(m[1]);
            einheit = m[2];
        }
    }

    let bezeichnung = textOf('bezeichnung');
    if (!bezeichnung) {
        // Fallback: alles, was keiner belegten Spalte zugeordnet ist.
        const used = bands.filter((b) => b.role !== 'ignore' && b.role !== 'bezeichnung');
        const rest = line.items
            .filter((it) => !used.some((b) => it.x >= b.x0 && it.x < b.x1))
            .map((it) => it.str)
            .join('')
            .replace(/\s+/g, ' ')
            .trim();
        bezeichnung = rest || line.text;
    }

    return { position, bezeichnung, einheit, menge, mengeText };
};
